import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const EditInvoicePage = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [formData, setFormData] = useState({
    clientId: null,
    invoiceNumber: '',
    invoiceDate: '',
    dueDate: '',
    lineItems: [],
    subTotal:    0,
    taxRate:     0,
    taxAmount:   0,
    totalAmount: 0,
    amountPaid:  0,
    status:      'draft',
    notes:        '',
    termsAndConditions: '',
    includeInTaxReport:  true,
  });
  
  const [isLoading, setIsLoading] = useState(true); 
  const [error, setError] = useState('');
  const [clients, setClients] = useState([]);
  const [originalInvoiceNumber, setOriginalInvoiceNumber] = useState('');

  useEffect(() => {
    const fetchClients = async () => {
      if (!token) return;
      try {
        const response = await fetch('/api/users?role=client', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch clients');
        const data = await response.json();
        if (data.status === 'success') {
          setClients(data.data.users || []);
        } else {
          throw new Error(data.message || 'Could not parse client data');
        }
      } catch (err) {
        console.error('Error fetching clients:', err);
      }
    };
    fetchClients();
  }, [token]);

  useEffect(() => {
    if (!invoiceId) {
      setError('No invoice ID provided.');
      setIsLoading(false);
      return;
    }
 
    (async () => {
      setIsLoading(true);
      setError('');
      setOriginalInvoiceNumber('');
      try {
        const res = await fetch(`/api/invoices/${invoiceId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || 'Invoice not found.');
        }
        const { data: { invoice } } = await res.json();
        setOriginalInvoiceNumber(invoice.invoiceNumber);
        
        // Ensure line items have proper numeric values
        const processedLineItems = (invoice.lineItems || []).map(item => ({
          ...item,
          quantity: parseFloat(item.quantity) || 0,
          unitPrice: parseFloat(item.unitPrice) || 0,
          total: parseFloat(item.total) || (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0)
        }));
        
        setFormData({
          clientId: invoice.client?.id || invoice.clientId,
          invoiceNumber: invoice.invoiceNumber,
          invoiceDate: invoice.invoiceDate?.split('T')[0] ?? '',
          dueDate:     invoice.dueDate?.split('T')[0]     ?? '',
          lineItems:   processedLineItems,
          subTotal:    parseFloat(invoice.subTotal || 0),
          taxRate:     parseFloat(invoice.taxRate || 0),
          taxAmount:   parseFloat(invoice.taxAmount || 0),
          totalAmount: parseFloat(invoice.totalAmount || 0),
          amountPaid:  parseFloat(invoice.amountPaid || invoice.paidAmount || 0),
          status:        invoice.status        || 'draft',
          notes:         invoice.notes         || '',
          termsAndConditions: invoice.termsAndConditions || '',
          includeInTaxReport:  invoice.includeInTaxReport  ?? true,
        });
      } catch (err) {
        console.error('Error loading invoice:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    })();
   }, [invoiceId, token]);

  // Recalculate totals whenever line items or tax rate changes
  useEffect(() => {
    let currentSubTotal = 0;
    formData.lineItems.forEach(item => {
      const itemTotal = parseFloat(item.total) || 0;
      currentSubTotal += itemTotal;
    });
    
    const taxRate = parseFloat(formData.taxRate) || 0;
    const currentTaxAmount = currentSubTotal * taxRate;
    const currentTotalAmount = currentSubTotal + currentTaxAmount;

    setFormData(prev => ({
      ...prev,
      subTotal: parseFloat(currentSubTotal.toFixed(2)),
      taxAmount: parseFloat(currentTaxAmount.toFixed(2)),
      totalAmount: parseFloat(currentTotalAmount.toFixed(2)),
    }));
  }, [formData.lineItems, formData.taxRate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleLineItemChange = (index, e) => {
    const { name, value, type } = e.target;
    const updatedLineItems = [...formData.lineItems];
    const item = { ...updatedLineItems[index] }; 

    // Parse numeric values properly
    if (type === 'number') {
      item[name] = parseFloat(value) || 0;
    } else {
      item[name] = value;
    }

    // Recalculate total when quantity or unit price changes
    if (name === 'quantity' || name === 'unitPrice') {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unitPrice) || 0;
      item.total = parseFloat((quantity * unitPrice).toFixed(2));
    }
    
    updatedLineItems[index] = item;
    setFormData(prev => ({ ...prev, lineItems: updatedLineItems }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { description: '', quantity: 1, unitPrice: 0, total: 0 }],
    }));
  };

  const removeLineItem = (index) => {
    const updatedLineItems = [...formData.lineItems];
    updatedLineItems.splice(index, 1);
    setFormData(prev => ({ ...prev, lineItems: updatedLineItems }));
  };

  const formatDateForAPI = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString + 'T00:00:00.000Z');
    return date.toISOString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const apiData = {
        ...formData,
        invoiceDate: formatDateForAPI(formData.invoiceDate),
        dueDate: formatDateForAPI(formData.dueDate),
        clientId: parseInt(formData.clientId, 10),
      };

      const response = await fetch(`/api/invoices/${invoiceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(apiData)
      });
      
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to update invoice.');
      }
      
      alert('Invoice updated successfully!');
      navigate(`/admin/invoices/view/${invoiceId}`);
    } catch (err) {
      console.error('Error updating invoice:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = "mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm";
  const inputSmClass = "mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"; 

  if (isLoading) { 
    return <div className="container mx-auto px-4 py-8 text-center">Loading invoice details...</div>;
  }

  if (error && !originalInvoiceNumber) { 
    return (
      <div className="container mx-auto px-4 py-8">
        <header className="mb-8">
            <h1 className="text-4xl font-bold text-brand-text-dark">Edit Invoice {invoiceId ? `(ID: ${invoiceId})` : ''}</h1>
            <button onClick={() => navigate(-1)} className="text-sm text-blue-600 hover:text-blue-800 mt-2">&larr; Back</button>
        </header>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
        </div>
        <button onClick={() => navigate("/admin/invoices")} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Go to Invoices</button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-8">
        <h1 className="text-4xl font-bold text-brand-text-dark">Edit Invoice {originalInvoiceNumber ? `#${originalInvoiceNumber}` : (formData.invoiceNumber || '')}</h1>
        <button onClick={() => navigate(`/admin/invoices/view/${invoiceId}`)} className="text-sm text-blue-600 hover:text-blue-800 mt-2">
          &larr; Back to View Invoice
        </button>
      </header>

      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6 md:p-8">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        <div className="mb-6">
          <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-1">Client</label>
          <select
            name="clientId"
            id="clientId"
            value={formData.clientId || ''}
            onChange={handleChange}
            className={inputClass}
            required
            disabled={!clients.length}
          >
            <option value="" disabled>Select a client</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.username} ({client.email})
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="invoiceNumber" className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
            <input type="text" name="invoiceNumber" id="invoiceNumber" value={formData.invoiceNumber} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label htmlFor="invoiceDate" className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
            <input type="date" name="invoiceDate" id="invoiceDate" value={formData.invoiceDate} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
            <input type="date" name="dueDate" id="dueDate" value={formData.dueDate} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select name="status" id="status" value={formData.status} onChange={handleChange} className={inputClass}>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="void">Void</option>
              <option value="partially_paid">Partially Paid</option> 
            </select>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Line Items</h3>
          {formData.lineItems.map((item, index) => (
            <div key={item.id || index} className="grid grid-cols-12 gap-x-3 gap-y-2 items-end p-3 mb-3 border rounded-md">
              <div className="col-span-12 sm:col-span-5">
                <label htmlFor={`itemDesc-${index}`} className="block text-xs font-medium text-gray-600">Description</label>
                <input 
                  type="text" 
                  name="description" 
                  id={`itemDesc-${index}`} 
                  value={item.description || ''} 
                  onChange={(e) => handleLineItemChange(index, e)} 
                  className={inputSmClass} 
                  placeholder="Service or Item" 
                  required 
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemQty-${index}`} className="block text-xs font-medium text-gray-600">Quantity</label>
                <input 
                  type="number" 
                  name="quantity" 
                  id={`itemQty-${index}`} 
                  value={item.quantity || 0} 
                  onChange={(e) => handleLineItemChange(index, e)} 
                  className={`${inputSmClass} text-right`} 
                  min="0.01" 
                  step="0.01" 
                  required 
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemPrice-${index}`} className="block text-xs font-medium text-gray-600">Unit Price</label>
                <input 
                  type="number" 
                  name="unitPrice" 
                  id={`itemPrice-${index}`} 
                  value={item.unitPrice || 0} 
                  onChange={(e) => handleLineItemChange(index, e)} 
                  className={`${inputSmClass} text-right`} 
                  min="0.00" 
                  step="0.01" 
                  required 
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemTotal-${index}`} className="block text-xs font-medium text-gray-600">Item Total</label>
                <input 
                  type="number" 
                  name="total" 
                  id={`itemTotal-${index}`} 
                  value={(item.total || 0).toFixed(2)} 
                  readOnly 
                  className={`${inputSmClass} text-right bg-gray-50`} 
                />
              </div>
              <div className="col-span-12 sm:col-span-1 flex justify-end">
                {formData.lineItems.length > 0 && (
                  <button type="button" onClick={() => removeLineItem(index)} className="text-red-500 hover:text-red-700 p-1 rounded-md text-sm">Remove</button>
                )}
              </div>
            </div>
          ))}
          <button type="button" onClick={addLineItem} className="mt-2 text-blue-600 hover:text-blue-800 font-medium text-sm py-2 px-3 border border-blue-500 rounded-md hover:bg-blue-50 transition-colors">
            + Add Line Item
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 border-t pt-6 mt-6">
          <div>
            <label htmlFor="subTotal" className="block text-sm font-medium text-gray-500">Subtotal</label>
            <input type="text" name="subTotal" id="subTotal" value={Number(formData.subTotal || 0).toFixed(2)} readOnly className={`${inputClass} bg-gray-50 text-right`} />
          </div>
          <div>
            <label htmlFor="taxRate" className="block text-sm font-medium text-gray-700">Tax Rate</label>
            <input type="number" name="taxRate" id="taxRate" value={formData.taxRate} onChange={handleChange} className={`${inputClass} text-right`} step="0.0001" />
          </div>
          <div>
            <label htmlFor="taxAmount" className="block text-sm font-medium text-gray-500">Tax Amount</label>
            <input type="text" name="taxAmount" id="taxAmount" value={Number(formData.taxAmount || 0).toFixed(2)} readOnly className={`${inputClass} bg-gray-50 text-right`} />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 border-t pt-6">
            <div>
                <label htmlFor="amountPaid" className="block text-sm font-medium text-gray-700">Amount Paid</label>
                <input type="number" name="amountPaid" id="amountPaid" value={formData.amountPaid} onChange={handleChange} className={`${inputClass} text-right`} step="0.01" min="0"/>
            </div>
            <div className="text-right">
                <p className="text-sm text-gray-500">Balance Due</p>
                <p className="text-2xl font-bold text-gray-800">${(Number(formData.totalAmount || 0) - Number(formData.amountPaid || 0)).toFixed(2)}</p>
            </div>
        </div>

        <div className="text-right mb-6 border-t pt-4">
          <p className="text-sm text-gray-500">Total Amount</p>
          <p className="text-2xl font-bold text-gray-800">${Number(formData.totalAmount || 0).toFixed(2)}</p>
        </div>

        <div className="mb-6">
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
          <textarea name="notes" id="notes" value={formData.notes} onChange={handleChange} rows="3" className={inputClass} placeholder="Internal notes or notes for the client..."></textarea>
        </div>

        <div className="mb-6">
          <label htmlFor="termsAndConditions" className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions (Optional)</label>
          <textarea name="termsAndConditions" id="termsAndConditions" value={formData.termsAndConditions} onChange={handleChange} rows="3" className={inputClass} placeholder="Define payment terms, payment methods, etc..."></textarea>
        </div>

        <div className="mb-8">
            <label htmlFor="includeInTaxReport" className="flex items-center">
                <input 
                    type="checkbox" 
                    name="includeInTaxReport" 
                    id="includeInTaxReport"
                    checked={formData.includeInTaxReport}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Include this invoice in tax reporting</span>
            </label>
        </div>
        
        <div className="flex justify-end pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate(`/admin/invoices/view/${invoiceId}`)}
            className="mr-3 px-6 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditInvoicePage;